import { useEffect, useRef, useState, useCallback } from "react";
import {
  Camera,
  CircleDot,
  Pause,
  Play,
  Square,
  RotateCcw,
  Download,
  Save,
  Maximize2,
  Minimize2,
  FlipHorizontal,
  Eye,
  EyeOff,
  Sliders,
  Video,
  VideoOff,
  Pencil,
  FileText,
  Sparkles,
  CheckCircle2,
  AlertCircle,
  X,
  Settings,
  SwitchCamera,
  Smartphone,
  Monitor,
  Square as SquareIcon,
  Mic,
  RotateCw,
  HelpCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/context/ConfirmContext";
import { createVideoCompositor, computeEffectiveRotation } from "@/lib/videoCompositor";

const MIN_SPEED = 15;
const MAX_SPEED = 200;

const RESOLUTION_PRESETS = [
  { id: "4k", label: "4K UHD", desc: "3840×2160", width: 3840, height: 2160 },
  { id: "1080p", label: "1080p HD", desc: "1920×1080", width: 1920, height: 1080 },
  { id: "720p", label: "720p HD", desc: "1280×720", width: 1280, height: 720 },
  { id: "480p", label: "480p SD", desc: "854×480", width: 854, height: 480 },
];

const ORIENTATION_OPTIONS = [
  { id: "landscape", label: "Landscape (16:9)", icon: Monitor, aspect: 16 / 9 },
  { id: "portrait", label: "Portrait (9:16)", icon: Smartphone, aspect: 9 / 16 },
  { id: "square", label: "Square (1:1)", icon: SquareIcon, aspect: 1 },
  { id: "auto", label: "Auto (Device)", icon: RotateCw, aspect: null },
];

const ROTATION_OPTIONS = [
  { id: "auto", label: "Auto (Sensor)" },
  { id: "0", label: "0° (Upright)" },
  { id: "90", label: "90° (Landscape)" },
  { id: "180", label: "180° (Invert)" },
  { id: "270", label: "270° (Landscape Alt)" },
];

function fmtTime(sec) {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}

export function RecordingStudio({
  script = "",
  title = "",
  contentId,
  onNavigateToEditor,
  onRecorded,
  onDownloadInitiated,
  onSaveAsset,
  saving = false,
}) {
  const { confirm } = useConfirm();
  const studioRef = useRef(null);
  const videoRef = useRef(null);
  const previewRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const compositorRef = useRef(null);
  const startTsRef = useRef(0);
  const elapsedBeforePauseRef = useRef(0);

  // Teleprompter refs
  const scrollRef = useRef(null);
  const scrollPosRef = useRef(0);
  const rafRef = useRef(null);
  const lastTsRef = useRef(0);
  const isTouchingRef = useRef(false);

  // Studio states
  const [permission, setPermission] = useState({ camera: null, mic: null, error: null });
  const [supported, setSupported] = useState(true);
  const [cameraActive, setCameraActive] = useState(false);
  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [blob, setBlob] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [mimeType, setMimeType] = useState("");
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Hardware devices & camera settings
  const [videoDevices, setVideoDevices] = useState([]);
  const [audioDevices, setAudioDevices] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState("");
  const [selectedMicId, setSelectedMicId] = useState("");
  const [facingMode, setFacingMode] = useState("user"); // "user" | "environment"
  const [qualityPreset, setQualityPreset] = useState("1080p");
  const [orientationMode, setOrientationMode] = useState("landscape"); // "landscape" | "portrait" | "square" | "auto"
  const [rotationMode, setRotationMode] = useState("auto"); // "auto" | "0" | "90" | "180" | "270"
  const [frameRate, setFrameRate] = useState(30);
  const [mirrorVideo, setMirrorVideo] = useState(true); // Mirror preview for front camera
  const [deviceOrientationIsLandscape, setDeviceOrientationIsLandscape] = useState(false);
  const [showLandscapeHint, setShowLandscapeHint] = useState(true);

  // Teleprompter settings
  const [prompterVisible, setPrompterVisible] = useState(true);
  const [practiceMode, setPracticeMode] = useState(false);
  const [prompterPlaying, setPrompterPlaying] = useState(false);
  const [speed, setSpeed] = useState(65); // px/sec
  const [fontSize, setFontSize] = useState(() => (typeof window !== "undefined" && window.innerWidth < 640 ? 18 : 24)); // px
  const [opacity, setOpacity] = useState(70); // %
  const [mirror, setMirror] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsTab, setSettingsTab] = useState("camera"); // "camera" | "prompter"

  // 1. Browser capability & device orientation check
  useEffect(() => {
    const ok =
      typeof navigator !== "undefined" &&
      navigator.mediaDevices &&
      typeof window !== "undefined" &&
      typeof window.MediaRecorder !== "undefined";
    setSupported(!!ok);

    const checkDeviceOrientation = () => {
      if (typeof window === "undefined") return;
      const winAngle = typeof window.orientation === "number" ? Math.abs(window.orientation) : 0;
      const screenAngle = Math.abs(window.screen?.orientation?.angle || 0);
      const isLand = window.innerWidth > window.innerHeight || winAngle === 90 || screenAngle === 90 || screenAngle === 270;
      setDeviceOrientationIsLandscape(isLand);
    };

    checkDeviceOrientation();
    window.addEventListener("resize", checkDeviceOrientation);
    window.addEventListener("orientationchange", checkDeviceOrientation);
    if (window.screen?.orientation) {
      window.screen.orientation.addEventListener("change", checkDeviceOrientation);
    }
    return () => {
      window.removeEventListener("resize", checkDeviceOrientation);
      window.removeEventListener("orientationchange", checkDeviceOrientation);
      if (window.screen?.orientation) {
        window.screen.orientation.removeEventListener("change", checkDeviceOrientation);
      }
    };
  }, []);

  // 2. Hardware device enumeration
  const refreshDevices = useCallback(async () => {
    try {
      if (!navigator.mediaDevices?.enumerateDevices) return;
      const devs = await navigator.mediaDevices.enumerateDevices();
      const vDevs = devs.filter((d) => d.kind === "videoinput");
      const aDevs = devs.filter((d) => d.kind === "audioinput");
      setVideoDevices(vDevs);
      setAudioDevices(aDevs);
    } catch (e) {
      console.warn("Could not list media devices", e);
    }
  }, []);

  useEffect(() => {
    refreshDevices();
    if (navigator.mediaDevices?.addEventListener) {
      navigator.mediaDevices.addEventListener("devicechange", refreshDevices);
      return () => {
        navigator.mediaDevices.removeEventListener("devicechange", refreshDevices);
      };
    }
  }, [refreshDevices]);

  // 3. Fullscreen event listener (all browser vendor events)
  useEffect(() => {
    const handleFsChange = () => {
      const fsElem =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;
      setIsFullscreen(!!fsElem);
    };
    document.addEventListener("fullscreenchange", handleFsChange);
    document.addEventListener("webkitfullscreenchange", handleFsChange);
    document.addEventListener("mozfullscreenchange", handleFsChange);
    document.addEventListener("MSFullscreenChange", handleFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFsChange);
      document.removeEventListener("webkitfullscreenchange", handleFsChange);
      document.removeEventListener("mozfullscreenchange", handleFsChange);
      document.removeEventListener("MSFullscreenChange", handleFsChange);
    };
  }, []);

  // 4. Recording Timer Loop
  useEffect(() => {
    let timerRaf;
    const tickTimer = () => {
      if (recording && !paused) {
        const nowElapsed = (Date.now() - startTsRef.current) / 1000 + elapsedBeforePauseRef.current;
        setSeconds(nowElapsed);
      }
      timerRaf = requestAnimationFrame(tickTimer);
    };
    timerRaf = requestAnimationFrame(tickTimer);
    return () => cancelAnimationFrame(timerRaf);
  }, [recording, paused]);

  // 5. Teleprompter Smooth Scroll Loop (High-precision accumulator for phone/120Hz screens)
  useEffect(() => {
    lastTsRef.current = 0;
    if (scrollRef.current) {
      scrollPosRef.current = scrollRef.current.scrollTop;
    }

    if (!prompterPlaying) {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return;
    }

    const tickScroll = (ts) => {
      if (!lastTsRef.current) {
        lastTsRef.current = ts;
      }
      const rawDt = (ts - lastTsRef.current) / 1000;
      const dt = Math.min(rawDt, 0.1); // clamp to 100ms max to prevent jumps
      lastTsRef.current = ts;

      const el = scrollRef.current;
      if (el && !isTouchingRef.current) {
        const maxScroll = el.scrollHeight - el.clientHeight;
        if (maxScroll > 10) {
          // Accumulate floating-point position so mobile integer truncation does not halt scroll
          scrollPosRef.current += speed * dt;

          if (scrollPosRef.current >= maxScroll - 2) {
            scrollPosRef.current = maxScroll;
            el.scrollTop = maxScroll;
            setPrompterPlaying(false);
            return;
          } else {
            el.scrollTop = scrollPosRef.current;
          }
        }
      }

      rafRef.current = requestAnimationFrame(tickScroll);
    };

    rafRef.current = requestAnimationFrame(tickScroll);
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [prompterPlaying, speed]);

  // 6. Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      if (compositorRef.current) {
        compositorRef.current.stop();
        compositorRef.current = null;
      }
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper: compute current effective orientation
  const isEffectiveLandscape =
    orientationMode === "landscape" ||
    (orientationMode === "auto" && deviceOrientationIsLandscape);
  const isEffectiveSquare = orientationMode === "square";

  // Target output dimensions helper
  const getTargetDimensions = (presetId, isLandscape, isSquare) => {
    const preset = RESOLUTION_PRESETS.find((p) => p.id === presetId) || RESOLUTION_PRESETS[1];
    if (isSquare) {
      const dim = Math.min(preset.width, preset.height);
      return { width: dim, height: dim };
    }
    if (isLandscape) {
      return { width: preset.width, height: preset.height };
    }
    return { width: preset.height, height: preset.width };
  };

  const screenAngle =
    typeof window !== "undefined"
      ? (window.screen?.orientation?.angle ?? (typeof window.orientation === "number" ? window.orientation : 0))
      : 0;

  const effectiveRotation = computeEffectiveRotation({
    rotationMode,
    isLandscape: isEffectiveLandscape,
    videoWidth: videoRef.current?.videoWidth || 0,
    videoHeight: videoRef.current?.videoHeight || 0,
    isDeviceLandscape: deviceOrientationIsLandscape,
    screenAngle,
  });

  const isRotated = effectiveRotation === 90 || effectiveRotation === 270;

  const cycleRotation = () => {
    if (recording) return;
    const modes = ["auto", "90", "180", "270", "0"];
    const idx = modes.indexOf(String(rotationMode));
    const next = modes[(idx + 1) % modes.length];
    setRotationMode(next);
  };

  // Build constraints helper
  const getCameraConstraints = useCallback(
    (camId, facing, quality, orMode, fps, micId) => {
      const selectedPreset =
        RESOLUTION_PRESETS.find((p) => p.id === quality) || RESOLUTION_PRESETS[1];
      const isLand =
        orMode === "landscape" ||
        (orMode === "auto" && (typeof window !== "undefined" && window.innerWidth > window.innerHeight));
      const isSq = orMode === "square";

      let targetW = selectedPreset.width;
      let targetH = selectedPreset.height;
      let aspect = 16 / 9;

      if (isSq) {
        targetW = Math.min(selectedPreset.width, selectedPreset.height);
        targetH = targetW;
        aspect = 1;
      } else if (!isLand) {
        // Portrait: swap width and height
        targetW = selectedPreset.height;
        targetH = selectedPreset.width;
        aspect = 9 / 16;
      }

      const videoConstraints = {
        width: { ideal: targetW },
        height: { ideal: targetH },
        aspectRatio: { ideal: aspect },
        frameRate: { ideal: fps || 30 },
      };

      if (camId) {
        videoConstraints.deviceId = { exact: camId };
      } else if (facing) {
        videoConstraints.facingMode = { ideal: facing };
      }

      const audioConstraints = micId ? { deviceId: { exact: micId } } : true;

      return {
        video: videoConstraints,
        audio: audioConstraints,
      };
    },
    []
  );

  // 7. Camera stream management
  const requestStream = async (overrideConstraints = null) => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }

      const constraints =
        overrideConstraints ||
        getCameraConstraints(
          selectedCameraId,
          facingMode,
          qualityPreset,
          orientationMode,
          frameRate,
          selectedMicId
        );

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      setPermission({ camera: "granted", mic: "granted", error: null });
      setCameraActive(true);
      setPracticeMode(false);
      setPrompterVisible(true);

      // Auto-detect front vs back camera to set appropriate mirror default
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        const settings = videoTrack.getSettings?.() || {};
        if (settings.facingMode) {
          setFacingMode(settings.facingMode);
          setMirrorVideo(settings.facingMode === "user");
        }
      }

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }

      // Re-enumerate devices with newly granted labels
      refreshDevices();
    } catch (e) {
      const name = e?.name || "";
      setPermission({
        camera: name === "NotAllowedError" ? "denied" : "error",
        mic: name === "NotAllowedError" ? "denied" : "error",
        error:
          name === "NotAllowedError"
            ? "Camera or microphone permission was denied. Please allow permissions in your browser bar."
            : name === "NotFoundError"
            ? "No camera device found on this machine."
            : name === "OverconstrainedError"
            ? "Selected camera resolution or settings are not supported by this device. Try 1080p or 720p."
            : "Unable to access the camera device. Check your browser settings.",
      });
      setCameraActive(false);
    }
  };

  const stopStream = () => {
    if (recording) {
      stopRecording();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
    setPracticeMode(false);
  };

  // Flip camera between front (user) and back (environment)
  const flipCamera = async () => {
    if (recording) {
      return;
    }

    let nextFacing = facingMode === "user" ? "environment" : "user";
    let nextCamId = "";

    // If multiple camera devices enumerated, select the opposing one
    if (videoDevices.length > 1) {
      const currentIdx = videoDevices.findIndex((d) => d.deviceId === selectedCameraId);
      const nextIdx = (currentIdx + 1) % videoDevices.length;
      nextCamId = videoDevices[nextIdx].deviceId;
    }

    setFacingMode(nextFacing);
    setSelectedCameraId(nextCamId);
    setMirrorVideo(nextFacing === "user");

    if (cameraActive) {
      const newConstraints = getCameraConstraints(
        nextCamId,
        nextFacing,
        qualityPreset,
        orientationMode,
        frameRate,
        selectedMicId
      );
      await requestStream(newConstraints);
    }
  };

  // Switch camera by device ID
  const handleSelectCamera = async (camId) => {
    setSelectedCameraId(camId);
    if (cameraActive && !recording) {
      const newConstraints = getCameraConstraints(
        camId,
        facingMode,
        qualityPreset,
        orientationMode,
        frameRate,
        selectedMicId
      );
      await requestStream(newConstraints);
    }
  };

  // Switch quality preset
  const handleSelectQuality = async (newQuality) => {
    setQualityPreset(newQuality);
    if (cameraActive && !recording) {
      const newConstraints = getCameraConstraints(
        selectedCameraId,
        facingMode,
        newQuality,
        orientationMode,
        frameRate,
        selectedMicId
      );
      await requestStream(newConstraints);
    }
  };

  // Switch orientation mode
  const handleSelectOrientation = async (newOrientation) => {
    setOrientationMode(newOrientation);
    if (cameraActive && !recording) {
      const newConstraints = getCameraConstraints(
        selectedCameraId,
        facingMode,
        qualityPreset,
        newOrientation,
        frameRate,
        selectedMicId
      );
      await requestStream(newConstraints);
    }
  };

  // Toggle quick landscape/portrait
  const toggleQuickOrientation = async () => {
    const nextMode = isEffectiveLandscape ? "portrait" : "landscape";
    await handleSelectOrientation(nextMode);
  };

  const pickMime = () => {
    const candidates = [
      "video/mp4;codecs=avc1,mp4a.40.2",
      "video/mp4;codecs=avc1",
      "video/mp4;codecs=h264,aac",
      "video/mp4",
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp8,opus",
      "video/webm",
    ];
    for (const c of candidates) {
      try {
        if (typeof window !== "undefined" && window.MediaRecorder && window.MediaRecorder.isTypeSupported(c)) {
          return c;
        }
      } catch {}
    }
    return "video/mp4";
  };

  // 8. Recording Controls
  const startRecording = async () => {
    if (!streamRef.current) {
      await requestStream();
    }
    if (!streamRef.current) return;

    chunksRef.current = [];
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlob(null);
    setBlobUrl(null);

    const mime = pickMime();
    setMimeType(mime);

    try {
      const target = getTargetDimensions(qualityPreset, isEffectiveLandscape, isEffectiveSquare);
      const curEffectiveRot = computeEffectiveRotation({
        rotationMode,
        isLandscape: isEffectiveLandscape,
        videoWidth: videoRef.current?.videoWidth || 0,
        videoHeight: videoRef.current?.videoHeight || 0,
        isDeviceLandscape: deviceOrientationIsLandscape,
        screenAngle,
      });

      let recordingStream = streamRef.current;
      const audioTracks = streamRef.current ? streamRef.current.getAudioTracks() : [];

      if (videoRef.current) {
        const comp = createVideoCompositor({
          video: videoRef.current,
          targetW: target.width,
          targetH: target.height,
          rotDeg: curEffectiveRot,
          mirror: mirrorVideo,
          fps: frameRate || 30,
          audioTracks,
        });
        if (comp && comp.canvasStream) {
          compositorRef.current = comp;
          recordingStream = comp.canvasStream;
        }
      }

      const rec = new window.MediaRecorder(recordingStream, mime ? { mimeType: mime } : undefined);
      recorderRef.current = rec;

      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      rec.onstop = () => {
        if (compositorRef.current) {
          compositorRef.current.stop();
          compositorRef.current = null;
        }
        const b = new Blob(chunksRef.current, { type: rec.mimeType || mime || "video/webm" });
        const url = URL.createObjectURL(b);
        setBlob(b);
        setBlobUrl(url);
        if (previewRef.current) {
          previewRef.current.src = url;
        }
        const elapsed = elapsedBeforePauseRef.current + (Date.now() - startTsRef.current) / 1000;
        const finalDuration = elapsed;
        elapsedBeforePauseRef.current = 0;
        setSeconds(finalDuration);
        onRecorded?.({
          blob: b,
          mimeType: rec.mimeType || mime || "video/webm",
          durationMs: Math.round(finalDuration * 1000),
          size: b.size,
          orientation: isEffectiveLandscape ? "landscape" : isEffectiveSquare ? "square" : "portrait",
          quality: qualityPreset,
        });
      };

      startTsRef.current = Date.now();
      elapsedBeforePauseRef.current = 0;
      setSeconds(0);
      rec.start(1000);
      setRecording(true);
      setPaused(false);
      if (hasScript && prompterVisible) {
        setPrompterPlaying(true);
      }
    } catch (err) {
      console.error("Failed to start MediaRecorder", err);
      if (compositorRef.current) {
        compositorRef.current.stop();
        compositorRef.current = null;
      }
    }
  };

  const pauseRecording = () => {
    if (!recorderRef.current || recorderRef.current.state !== "recording") return;
    recorderRef.current.pause();
    elapsedBeforePauseRef.current += (Date.now() - startTsRef.current) / 1000;
    setPaused(true);
    setPrompterPlaying(false);
  };

  const resumeRecording = () => {
    if (!recorderRef.current || recorderRef.current.state !== "paused") return;
    recorderRef.current.resume();
    startTsRef.current = Date.now();
    setPaused(false);
    if (hasScript && prompterVisible) {
      setPrompterPlaying(true);
    }
  };

  const stopRecording = () => {
    if (!recorderRef.current || recorderRef.current.state === "inactive") return;
    recorderRef.current.stop();
    if (compositorRef.current) {
      compositorRef.current.stop();
      compositorRef.current = null;
    }
    setRecording(false);
    setPaused(false);
    setPrompterPlaying(false);
  };

  const retake = async () => {
    if (blob) {
      const ok = await confirm({
        title: "Record another take?",
        description: "Your current unsaved take will be reset.",
        confirmText: "Retake",
        destructive: false,
      });
      if (!ok) return;
    }
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlob(null);
    setBlobUrl(null);
    setSeconds(0);
    if (previewRef.current) previewRef.current.removeAttribute("src");
  };

  const filenameForBlob = () => {
    const slug = (title || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "take";
    const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    const orientTag = isEffectiveLandscape ? "16x9" : isEffectiveSquare ? "1x1" : "9x16";
    return `${slug}-${orientTag}-${qualityPreset}-${ts}.mp4`;
  };

  const downloadTake = () => {
    if (!blob || !blobUrl) return;
    const filename = filenameForBlob();
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    onDownloadInitiated?.({ filename, fileType: "video/mp4" });
  };

  const saveAsset = () => {
    if (!blob) return;
    const filename = filenameForBlob();
    onSaveAsset?.({
      filename,
      mimeType: "video/mp4",
      sizeBytes: blob.size,
      durationMs: Math.round(seconds * 1000),
      blobUrl,
      blob,
      orientation: isEffectiveLandscape ? "landscape" : isEffectiveSquare ? "square" : "portrait",
      quality: qualityPreset,
    });
  };

  // 9. Teleprompter Helpers
  const resetPrompter = () => {
    scrollPosRef.current = 0;
    lastTsRef.current = 0;
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    setPrompterPlaying(false);
  };

  // 10. Fullscreen Toggle (supports all browser fullscreen APIs + CSS fallback)
  const toggleFullscreen = useCallback(async () => {
    if (!studioRef.current) return;
    try {
      const isCurrentlyFs = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );

      if (!isCurrentlyFs) {
        const el = studioRef.current;
        if (el.requestFullscreen) {
          await el.requestFullscreen();
        } else if (el.webkitRequestFullscreen) {
          await el.webkitRequestFullscreen();
        } else if (el.mozRequestFullScreen) {
          await el.mozRequestFullScreen();
        } else if (el.msRequestFullscreen) {
          await el.msRequestFullscreen();
        } else {
          setIsFullscreen((prev) => !prev);
        }
      } else {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          await document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          await document.msExitFullscreen();
        } else {
          setIsFullscreen(false);
        }
      }
    } catch (err) {
      console.warn("Fullscreen toggle failed, using CSS fallback", err);
      setIsFullscreen((prev) => !prev);
    }
  }, []);

  if (!supported) {
    return (
      <div className="rounded-xl border border-border bg-surface p-6" data-testid="camera-unsupported">
        <p className="text-sm text-destructive">Your browser does not support WebRTC MediaRecorder video recording.</p>
      </div>
    );
  }

  const hasScript = (script || "").trim().length > 0;

  // Determine stage aspect ratio styling
  const stageAspectClass = isFullscreen
    ? isEffectiveLandscape
      ? "h-full w-full max-h-full"
      : isEffectiveSquare
      ? "h-full aspect-square max-h-full mx-auto"
      : "h-full aspect-[9/16] max-h-full mx-auto"
    : isEffectiveLandscape
    ? "aspect-video w-full max-h-[75vh]"
    : isEffectiveSquare
    ? "aspect-square w-full max-w-[480px] mx-auto max-h-[75vh]"
    : "aspect-[9/16] w-full max-w-[400px] sm:max-w-[440px] mx-auto max-h-[75vh]";

  return (
    <div className="space-y-6">
      {/* RECORDING STUDIO HERO CANVAS */}
      <div
        ref={studioRef}
        data-testid="camera-recorder"
        className={`relative overflow-hidden rounded-2xl border border-border bg-zinc-950 shadow-2xl transition-all ${
          isFullscreen
            ? "fixed inset-0 z-50 !rounded-none !border-0 h-screen w-screen max-h-screen max-w-screen flex flex-col justify-start"
            : "w-full"
        }`}
      >
        {/* Top Studio Control Bar */}
        <div className="relative z-30 flex items-center justify-between gap-2 border-b border-white/10 bg-zinc-950/90 px-2.5 sm:px-4 py-2 backdrop-blur-md min-w-0">
          {/* Left: Studio Identity & Live Recording Badge */}
          <div className="flex items-center gap-1.5 sm:gap-2.5 min-w-0 shrink">
            <div className="flex items-center gap-1.5 shrink-0">
              <span className="flex h-2 w-2 rounded-full bg-accent animate-pulse" />
              <span className="font-display text-xs sm:text-sm font-bold tracking-tight text-white">
                <span className="hidden xs:inline">Recording </span>Studio
              </span>
            </div>

            {/* Recording State Pill */}
            {recording && !paused && (
              <div className="flex items-center gap-1 sm:gap-1.5 rounded-full border border-red-500/40 bg-red-500/20 px-2 sm:px-2.5 py-0.5 text-[10px] sm:text-xs font-bold uppercase tracking-wider text-red-400 animate-pulse shrink-0">
                <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                <span>REC</span>
                <span className="tabular-nums font-mono text-white" data-testid="camera-timer">
                  {fmtTime(seconds)}
                </span>
              </div>
            )}

            {recording && paused && (
              <div className="flex items-center gap-1 sm:gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/20 px-2 sm:px-2.5 py-0.5 text-[10px] sm:text-xs font-bold uppercase tracking-wider text-amber-400 shrink-0">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                <span>PAUSED</span>
                <span className="tabular-nums font-mono text-white" data-testid="camera-timer">
                  {fmtTime(seconds)}
                </span>
              </div>
            )}

            {!recording && cameraActive && (
              <div className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-400 shrink-0">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                <span>{qualityPreset.toUpperCase()}</span>
                <span>·</span>
                <span>{isEffectiveLandscape ? "16:9" : isEffectiveSquare ? "1:1" : "9:16"}</span>
              </div>
            )}
          </div>

          {/* Right: Studio Controls (Teleprompter, Camera Settings, Camera Toggle, Fullscreen) */}
          <div className="flex items-center gap-1 sm:gap-1.5 shrink-0 overflow-x-auto [scrollbar-width:none]">
            {/* Quick Flip Camera (Front/Back) */}
            {cameraActive && !recording && (
              <Button
                size="sm"
                variant="ghost"
                onClick={flipCamera}
                title="Flip Camera (Front / Back)"
                className="hidden sm:inline-flex h-7 sm:h-8 px-1.5 sm:px-2 text-xs font-medium text-zinc-300 hover:bg-white/10 hover:text-white shrink-0"
              >
                <SwitchCamera className="h-3.5 w-3.5 sm:mr-1" />
                <span className="hidden md:inline">Flip</span>
              </Button>
            )}

            {/* Quick Rotate Camera (Auto / 0 / 90 / 180 / 270) */}
            {cameraActive && !recording && (
              <Button
                size="sm"
                variant="ghost"
                onClick={cycleRotation}
                title={`Rotate video 90° (Currently: ${rotationMode === "auto" ? `Auto (${effectiveRotation}°)` : `${rotationMode}°`})`}
                className={`hidden sm:inline-flex h-7 sm:h-8 px-1.5 sm:px-2 text-xs font-medium text-zinc-300 hover:bg-white/10 hover:text-white shrink-0 ${
                  effectiveRotation !== 0 ? "text-accent bg-accent/15" : ""
                }`}
              >
                <RotateCw className="h-3.5 w-3.5 sm:mr-1" />
                <span className="hidden md:inline">Rotate</span>
                {effectiveRotation !== 0 && (
                  <span className="text-[10px] text-accent font-bold sm:ml-0.5">{effectiveRotation}°</span>
                )}
              </Button>
            )}

            {/* Quick Orientation Switcher (16:9 Landscape / 9:16 Portrait) */}
            {!recording && (
              <Button
                size="sm"
                variant="ghost"
                onClick={toggleQuickOrientation}
                title={`Switch format: currently ${isEffectiveLandscape ? "Landscape 16:9" : "Portrait 9:16"}`}
                className="h-7 sm:h-8 px-1.5 sm:px-2 text-xs font-medium text-zinc-300 hover:bg-white/10 hover:text-white shrink-0"
              >
                {isEffectiveLandscape ? (
                  <>
                    <Monitor className="h-3.5 w-3.5 text-accent sm:mr-1" />
                    <span className="text-[11px] font-semibold text-accent">16:9</span>
                  </>
                ) : (
                  <>
                    <Smartphone className="h-3.5 w-3.5 text-accent sm:mr-1" />
                    <span className="text-[11px] font-semibold text-accent">9:16</span>
                  </>
                )}
              </Button>
            )}

            {/* Teleprompter Visibility Toggle */}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                if (cameraActive) {
                  setPrompterVisible((v) => !v);
                } else {
                  setPracticeMode((p) => !p);
                }
              }}
              data-testid="camera-toggle-prompter"
              className={`h-7 sm:h-8 px-2 sm:px-2.5 text-xs font-medium text-white hover:bg-white/10 shrink-0 ${
                (cameraActive ? prompterVisible : practiceMode) ? "bg-white/15 text-accent font-semibold" : "text-zinc-400"
              }`}
              title={
                cameraActive
                  ? prompterVisible
                    ? "Hide Teleprompter"
                    : "Show Teleprompter"
                  : practiceMode
                  ? "Exit Practice"
                  : "Practice Script"
              }
            >
              {(cameraActive ? prompterVisible : practiceMode) ? (
                <>
                  <Eye className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Prompter</span>
                </>
              ) : (
                <>
                  <EyeOff className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Prompter</span>
                </>
              )}
            </Button>

            {/* Studio Settings Drawer Toggle */}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowSettings((s) => !s)}
              className={`h-7 sm:h-8 px-2 sm:px-2.5 text-xs font-medium text-white hover:bg-white/10 shrink-0 ${
                showSettings ? "bg-white/15 text-accent font-bold" : "text-zinc-300"
              }`}
              title="Studio Settings"
            >
              <Settings className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Settings</span>
            </Button>

            {/* Camera Power Button */}
            {cameraActive ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={stopStream}
                className="h-7 sm:h-8 px-2 sm:px-2.5 text-xs font-medium text-zinc-300 hover:bg-red-500/20 hover:text-red-400 shrink-0"
                title="Turn off camera"
              >
                <VideoOff className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Off</span>
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={() => requestStream()}
                data-testid="camera-request-permission"
                className="h-7 sm:h-8 border-accent/40 bg-accent/15 px-2 sm:px-2.5 text-xs font-semibold text-accent hover:bg-accent hover:text-accent-foreground shrink-0"
              >
                <Video className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Camera</span>
              </Button>
            )}

            {/* Fullscreen Button */}
            <Button
              size="sm"
              variant="ghost"
              onClick={toggleFullscreen}
              data-testid="teleprompter-full"
              className="h-7 sm:h-8 px-1.5 sm:px-2 text-xs font-medium text-white hover:bg-white/10 shrink-0"
              title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            >
              {isFullscreen ? (
                <Minimize2 className="h-3.5 w-3.5" />
              ) : (
                <Maximize2 className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
        </div>

        {/* COMPREHENSIVE STUDIO SETTINGS DRAWER (Collapsible) */}
        {showSettings && (
          <div className="relative z-30 border-b border-white/10 bg-zinc-900/95 px-3 sm:px-5 py-3 text-xs text-zinc-200 backdrop-blur-md">
            {/* Settings Tabs Header */}
            <div className="mb-3 flex items-center justify-between border-b border-white/10 pb-2">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSettingsTab("camera")}
                  className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-semibold transition-all ${
                    settingsTab === "camera"
                      ? "bg-accent text-accent-foreground"
                      : "text-zinc-400 hover:text-white"
                  }`}
                >
                  <Camera className="h-3.5 w-3.5" />
                  <span>Camera &amp; Format</span>
                </button>
                <button
                  type="button"
                  onClick={() => setSettingsTab("prompter")}
                  className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-semibold transition-all ${
                    settingsTab === "prompter"
                      ? "bg-accent text-accent-foreground"
                      : "text-zinc-400 hover:text-white"
                  }`}
                >
                  <FileText className="h-3.5 w-3.5" />
                  <span>Teleprompter</span>
                </button>
              </div>
              <button
                type="button"
                onClick={() => setShowSettings(false)}
                className="rounded p-1 text-zinc-400 hover:bg-white/10 hover:text-white"
                title="Close settings"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* TAB 1: Camera & Recording Format Settings */}
            {settingsTab === "camera" && (
              <div className="space-y-3.5">
                {/* Row 1: Camera Device & Quick Flip */}
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                      Camera Device
                    </label>
                    <div className="flex items-center gap-2">
                      <select
                        value={selectedCameraId}
                        onChange={(e) => handleSelectCamera(e.target.value)}
                        disabled={recording}
                        className="h-8 flex-1 rounded-md border border-white/15 bg-zinc-800 px-2.5 text-xs text-white outline-none focus:border-accent"
                      >
                        <option value="">Default / Auto Camera</option>
                        {videoDevices.map((d, i) => (
                          <option key={d.deviceId || i} value={d.deviceId}>
                            {d.label || `Camera ${i + 1}`}
                          </option>
                        ))}
                      </select>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={flipCamera}
                        disabled={recording}
                        className="h-8 border-white/20 bg-white/5 px-2.5 text-xs text-white hover:bg-white/15 shrink-0"
                        title="Flip Front / Back Camera"
                      >
                        <SwitchCamera className="mr-1 h-3.5 w-3.5" />
                        <span>Flip</span>
                      </Button>
                    </div>
                  </div>

                  {/* Audio Microphone Device */}
                  <div>
                    <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                      Microphone
                    </label>
                    <select
                      value={selectedMicId}
                      onChange={(e) => setSelectedMicId(e.target.value)}
                      disabled={recording}
                      className="h-8 w-full rounded-md border border-white/15 bg-zinc-800 px-2.5 text-xs text-white outline-none focus:border-accent"
                    >
                      <option value="">Default System Microphone</option>
                      {audioDevices.map((d, i) => (
                        <option key={d.deviceId || i} value={d.deviceId}>
                          {d.label || `Microphone ${i + 1}`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Row 2: Video Orientation & Aspect Ratio (Landscape vs Portrait vs Square) */}
                <div>
                  <div className="mb-1.5 flex items-center justify-between">
                    <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                      Orientation &amp; Format
                    </label>
                    <span className="text-[11px] text-accent font-medium">
                      {isEffectiveLandscape
                        ? "Widescreen 16:9 (Guaranteed Landscape Video)"
                        : isEffectiveSquare
                        ? "Square 1:1 (Social Post)"
                        : "Vertical 9:16 (Shorts / Reels / TikTok)"}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {ORIENTATION_OPTIONS.map((opt) => {
                      const Icon = opt.icon;
                      const active = orientationMode === opt.id;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => handleSelectOrientation(opt.id)}
                          disabled={recording}
                          className={`flex items-center justify-center gap-1.5 rounded-lg border py-2 px-3 text-xs font-semibold transition-all ${
                            active
                              ? "border-accent bg-accent/20 text-accent ring-1 ring-accent"
                              : "border-white/15 bg-zinc-800/80 text-zinc-300 hover:border-white/30 hover:bg-zinc-800"
                          } ${recording ? "opacity-60 cursor-not-allowed" : ""}`}
                        >
                          <Icon className="h-3.5 w-3.5" />
                          <span>{opt.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Row 2.5: Camera Sensor Rotation */}
                <div>
                  <div className="mb-1.5 flex items-center justify-between">
                    <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                      Camera Sensor Rotation
                    </label>
                    <span className="text-[11px] text-accent font-medium">
                      {rotationMode === "auto" ? `Auto Detected (${effectiveRotation}°)` : `${rotationMode}° Fixed`}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5">
                    {ROTATION_OPTIONS.map((rot) => (
                      <button
                        key={rot.id}
                        type="button"
                        onClick={() => setRotationMode(rot.id)}
                        disabled={recording}
                        className={`rounded-lg border py-1.5 px-2 text-center text-xs font-semibold transition-all ${
                          rotationMode === rot.id
                            ? "border-accent bg-accent/20 text-accent ring-1 ring-accent"
                            : "border-white/15 bg-zinc-800/80 text-zinc-300 hover:bg-zinc-800"
                        } ${recording ? "opacity-60 cursor-not-allowed" : ""}`}
                      >
                        {rot.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Row 3: Quality / Resolution Presets & Frame Rate */}
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                      Recording Quality
                    </label>
                    <div className="grid grid-cols-4 gap-1.5">
                      {RESOLUTION_PRESETS.map((p) => {
                        const active = qualityPreset === p.id;
                        return (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => handleSelectQuality(p.id)}
                            disabled={recording}
                            className={`rounded-lg border py-1.5 px-2 text-center text-xs font-semibold transition-all ${
                              active
                                ? "border-accent bg-accent text-accent-foreground shadow-sm"
                                : "border-white/15 bg-zinc-800/80 text-zinc-300 hover:bg-zinc-800"
                            } ${recording ? "opacity-60 cursor-not-allowed" : ""}`}
                          >
                            <span className="block">{p.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Frame Rate & Preview Mirror */}
                  <div>
                    <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
                      Framerate &amp; Mirror
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1 rounded-md border border-white/15 bg-zinc-800 p-0.5">
                        {[30, 60].map((fps) => (
                          <button
                            key={fps}
                            type="button"
                            onClick={() => setFrameRate(fps)}
                            disabled={recording}
                            className={`rounded px-2.5 py-1 text-xs font-medium transition-all ${
                              frameRate === fps
                                ? "bg-accent text-white font-bold"
                                : "text-zinc-400 hover:text-white"
                            }`}
                          >
                            {fps} fps
                          </button>
                        ))}
                      </div>

                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setMirrorVideo((m) => !m)}
                        className={`h-8 border border-white/15 text-xs ${
                          mirrorVideo ? "bg-accent/20 text-accent font-semibold" : "bg-zinc-800 text-zinc-300"
                        }`}
                        title="Mirror Camera Preview (for Selfie / Front Camera)"
                      >
                        <FlipHorizontal className="mr-1 h-3.5 w-3.5" />
                        <span>Mirror Cam</span>
                      </Button>
                    </div>
                  </div>
                </div>

                {/* iPhone Landscape Help Banner */}
                {isEffectiveLandscape && (
                  <div className="rounded-lg border border-accent/25 bg-accent/10 p-2.5 text-[11px] text-zinc-300 flex items-start gap-2">
                    <Monitor className="h-4 w-4 shrink-0 text-accent mt-0.5" />
                    <div>
                      <span className="font-semibold text-white">iPhone Front &amp; Back Camera Landscape: </span>
                      <span>
                        Widescreen (16:9) is guaranteed. Turn your iPhone horizontally or tap &quot;Rotate&quot; to align the camera feed. The compositing engine renders the stream directly to 16:9 landscape video for YouTube.
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* TAB 2: Teleprompter Controls */}
            {settingsTab === "prompter" && (
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 sm:gap-4 items-center">
                {/* Speed Slider */}
                <label className="flex items-center gap-2 justify-between sm:justify-start">
                  <span className="text-zinc-400 font-medium">Speed</span>
                  <input
                    type="range"
                    min={MIN_SPEED}
                    max={MAX_SPEED}
                    value={speed}
                    onChange={(e) => setSpeed(Number(e.target.value))}
                    data-testid="teleprompter-speed"
                    className="h-1.5 w-24 cursor-pointer accent-accent"
                  />
                  <span className="tabular-nums font-mono text-zinc-200">{speed}px/s</span>
                </label>

                {/* Font Size Slider */}
                <label className="flex items-center gap-2 justify-between sm:justify-start">
                  <span className="text-zinc-400 font-medium">Font</span>
                  <input
                    type="range"
                    min={14}
                    max={48}
                    value={fontSize}
                    onChange={(e) => setFontSize(Number(e.target.value))}
                    data-testid="teleprompter-font"
                    className="h-1.5 w-20 cursor-pointer accent-accent"
                  />
                  <span className="tabular-nums font-mono text-zinc-200">{fontSize}px</span>
                </label>

                {/* Overlay Opacity Slider */}
                <label className="flex items-center gap-2 justify-between sm:justify-start">
                  <span className="text-zinc-400 font-medium">Opacity</span>
                  <input
                    type="range"
                    min={25}
                    max={95}
                    value={opacity}
                    onChange={(e) => setOpacity(Number(e.target.value))}
                    className="h-1.5 w-20 cursor-pointer accent-accent"
                  />
                  <span className="tabular-nums font-mono text-zinc-200">{opacity}%</span>
                </label>

                {/* Mirror Toggle */}
                <div className="flex items-center justify-end">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setMirror((m) => !m)}
                    data-testid="teleprompter-mirror"
                    aria-pressed={mirror}
                    className={`h-7 px-3 text-xs ${mirror ? "bg-accent/20 text-accent font-semibold" : "text-zinc-300 hover:bg-white/10"}`}
                  >
                    <FlipHorizontal className="mr-1 h-3 w-3" /> Mirror Text
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Permission Error Banner */}
        {permission.error && (
          <div
            className="relative z-30 m-3 sm:m-4 flex items-center gap-2.5 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive"
            data-testid="camera-permission-error"
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{permission.error}</span>
          </div>
        )}

        {/* MAIN STUDIO CAMERA & OVERLAY STAGE (Responsive Aspect Ratio) */}
        <div className={`relative w-full flex items-center justify-center bg-black overflow-hidden ${
          isFullscreen ? "flex-1 min-h-0 h-full w-full" : "py-0 sm:py-1"
        }`}>
          <div className={`relative ${stageAspectClass} flex items-center justify-center bg-zinc-950 overflow-hidden transition-all duration-300 ${
            isFullscreen ? "h-full" : ""
          }`}>
            {/* Live Video Feed with Mirror & Rotation transformation */}
            <video
              ref={videoRef}
              playsInline
              webkit-playsinline="true"
              muted
              autoPlay
              className={`absolute inset-0 h-full w-full object-cover transition-transform duration-300 ${
                cameraActive ? "opacity-100" : "opacity-0 pointer-events-none"
              }`}
              style={{
                transform: [
                  mirrorVideo ? "scaleX(-1)" : "",
                  effectiveRotation !== 0 ? `rotate(${effectiveRotation}deg)` : "",
                  isRotated ? "scale(1.778)" : "",
                ].filter(Boolean).join(" ") || undefined,
              }}
              data-testid="camera-live-preview"
            />

            {/* Quick Floating Pill on Camera Stage (Flip Camera, Rotate & Orientation) - ONLY shown when Teleprompter is NOT open */}
            {cameraActive && !prompterVisible && !practiceMode && (
              <div className="absolute top-2.5 right-2.5 z-20 flex items-center gap-1 rounded-full border border-white/20 bg-zinc-950/85 p-1 backdrop-blur-md shadow-xl">
                <button
                  type="button"
                  onClick={flipCamera}
                  disabled={recording}
                  className="flex h-7 w-7 items-center justify-center rounded-full text-zinc-300 transition-all hover:bg-white/15 hover:text-white"
                  title="Flip camera (Front / Back)"
                >
                  <SwitchCamera className="h-3.5 w-3.5" />
                </button>

                <button
                  type="button"
                  onClick={cycleRotation}
                  disabled={recording}
                  className={`flex h-7 items-center gap-1 px-2 rounded-full text-zinc-300 transition-all hover:bg-white/15 hover:text-white ${
                    effectiveRotation !== 0 ? "text-accent font-bold bg-accent/20" : ""
                  }`}
                  title={`Rotate video 90° (Currently: ${rotationMode === "auto" ? `Auto (${effectiveRotation}°)` : `${rotationMode}°`})`}
                >
                  <RotateCw className="h-3.5 w-3.5" />
                  {effectiveRotation !== 0 && (
                    <span className="text-[10px] text-accent font-bold">{effectiveRotation}°</span>
                  )}
                </button>

                <button
                  type="button"
                  onClick={toggleQuickOrientation}
                  disabled={recording}
                  className="flex h-7 items-center gap-1 px-2 rounded-full text-zinc-300 transition-all hover:bg-white/15 hover:text-white"
                  title={`Toggle orientation (Currently ${isEffectiveLandscape ? "16:9 Landscape" : "9:16 Portrait"})`}
                >
                  {isEffectiveLandscape ? (
                    <Monitor className="h-3.5 w-3.5 text-accent" />
                  ) : (
                    <Smartphone className="h-3.5 w-3.5 text-accent" />
                  )}
                  <span className="text-[11px] font-semibold text-accent">
                    {isEffectiveLandscape ? "16:9" : "9:16"}
                  </span>
                </button>
              </div>
            )}

            {/* Camera Disabled State (Clean Studio Backdrop - only shown when not practicing) */}
            {!cameraActive && !practiceMode && (
              <div className="absolute inset-0 flex flex-col items-center justify-center p-4 sm:p-6 text-center z-10">
                <div className="flex h-12 w-12 sm:h-16 sm:w-16 items-center justify-center rounded-2xl border border-white/10 bg-zinc-900/90 text-accent shadow-2xl backdrop-blur-md mb-2.5 sm:mb-4 shrink-0">
                  <Camera className="h-6 w-6 sm:h-8 sm:w-8 animate-pulse" />
                </div>
                <h3 className="font-display text-base sm:text-lg font-bold text-white tracking-tight">Camera Studio Standby</h3>
                <p className="mt-1 sm:mt-1.5 w-full max-w-xs sm:max-w-md text-xs sm:text-sm leading-relaxed text-zinc-400 break-words px-2">
                  Record high-retention video in {qualityPreset.toUpperCase()} {isEffectiveLandscape ? "Landscape (16:9)" : "Portrait (9:16)"} with real-time scrolling teleprompter.
                </p>
                <div className="mt-4 sm:mt-5 flex flex-col sm:flex-row items-center justify-center gap-2.5 sm:gap-3 w-full max-w-xs sm:max-w-md px-2">
                  <Button
                    onClick={() => requestStream()}
                    data-testid="camera-request-permission"
                    className="w-full sm:w-auto h-9 sm:h-10 bg-accent px-5 sm:px-6 text-xs sm:text-sm font-semibold text-accent-foreground hover:bg-accent-hover shadow-lg shadow-accent/20"
                  >
                    <Video className="mr-1.5 sm:mr-2 h-4 w-4" /> Enable Camera
                  </Button>
                  {hasScript && (
                    <Button
                      variant="outline"
                      onClick={() => setPracticeMode(true)}
                      className="w-full sm:w-auto h-9 sm:h-10 border-white/20 bg-white/5 px-4 sm:px-5 text-xs sm:text-sm font-medium text-white hover:bg-white/15"
                    >
                      <FileText className="mr-1.5 sm:mr-2 h-4 w-4 text-accent" /> Practice Teleprompter
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* TELEPROMPTER OVERLAY (Centered over camera feed or in practice mode) */}
            {(cameraActive ? prompterVisible : practiceMode) && (
              <div className="pointer-events-none absolute inset-x-2 top-2 bottom-16 sm:inset-x-4 sm:top-3 sm:bottom-20 md:inset-x-8 flex justify-center z-20">
                <div
                  className="pointer-events-auto flex flex-col w-full max-w-2xl overflow-hidden rounded-xl border border-white/15 shadow-2xl transition-all"
                  style={{
                    backgroundColor: `rgba(0, 0, 0, ${opacity / 100})`,
                    backdropFilter: "blur(14px)",
                  }}
                  data-testid="teleprompter"
                >
                  {/* Prompter Mini Header / Controls */}
                  <div className="flex items-center justify-between border-b border-white/10 px-2.5 py-1.5 sm:px-3 sm:py-2 bg-black/70 text-xs gap-1.5 shrink-0">
                    <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                      <span className="font-semibold uppercase tracking-wider text-zinc-300 flex items-center gap-1 text-[11px] sm:text-xs shrink-0">
                        <FileText className="h-3 sm:h-3.5 w-3 sm:w-3.5 text-accent" /> <span className="hidden xs:inline">Teleprompter</span><span className="xs:hidden">Script</span>
                      </span>
                      {!cameraActive && (
                        <span className="rounded-full bg-accent/20 px-1.5 py-0.5 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider text-accent shrink-0">
                          Practice
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 sm:gap-1.5 shrink-0">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setPrompterPlaying((p) => !p)}
                        disabled={!hasScript}
                        data-testid="teleprompter-play"
                        className={`h-7 px-2 sm:px-2.5 text-[11px] font-semibold text-white transition-colors shrink-0 ${
                          prompterPlaying ? "bg-accent text-accent-foreground" : "bg-white/15 hover:bg-white/25"
                        }`}
                      >
                        {prompterPlaying ? (
                          <>
                            <Pause className="mr-1 h-3 w-3" /> Pause
                          </>
                        ) : (
                          <>
                            <Play className="mr-1 h-3 w-3" /> Scroll
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={resetPrompter}
                        disabled={!hasScript}
                        data-testid="teleprompter-reset"
                        className="h-7 px-2 text-[11px] text-zinc-300 hover:bg-white/15 shrink-0"
                        title="Reset Teleprompter to Top"
                      >
                        <RotateCcw className="mr-1 h-3 w-3" /> <span className="hidden sm:inline">Reset</span>
                      </Button>
                      {!cameraActive && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setPracticeMode(false)}
                          className="h-7 px-1.5 text-[11px] text-zinc-400 hover:text-white shrink-0"
                          title="Close Practice Mode"
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Prompter Scrolling Text Area with smooth gradient fade masks */}
                  <div
                    ref={scrollRef}
                    onTouchStart={() => {
                      isTouchingRef.current = true;
                    }}
                    onTouchEnd={() => {
                      isTouchingRef.current = false;
                      if (scrollRef.current) {
                        scrollPosRef.current = scrollRef.current.scrollTop;
                      }
                      lastTsRef.current = 0;
                    }}
                    onScroll={() => {
                      if (isTouchingRef.current && scrollRef.current) {
                        scrollPosRef.current = scrollRef.current.scrollTop;
                      }
                    }}
                    className="flex-1 overflow-y-auto px-4 pt-6 pb-20 sm:px-6 sm:pt-7 sm:pb-24 focus:outline-none [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                    style={{
                      fontSize: `${fontSize}px`,
                      transform: mirror ? "scaleX(-1)" : undefined,
                      maskImage: "linear-gradient(to bottom, transparent 0%, black 28px, black calc(100% - 28px), transparent 100%)",
                      WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 28px, black calc(100% - 28px), transparent 100%)",
                    }}
                    data-testid="teleprompter-scroll"
                  >
                    {hasScript ? (
                      <div className="space-y-3 sm:space-y-4 whitespace-pre-wrap font-sans font-medium text-white leading-relaxed drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                        {script}
                        {/* Space at the bottom for smooth scroll-off */}
                        <div className="h-28 sm:h-36" aria-hidden />
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-8 sm:py-10 text-center">
                        <p className="text-xs sm:text-sm font-medium text-zinc-300">No script written yet</p>
                        <p className="mt-1 text-[11px] sm:text-xs text-zinc-400">
                          Write your draft in the Content editor to read it on the teleprompter.
                        </p>
                        {onNavigateToEditor && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={onNavigateToEditor}
                            className="mt-3 h-7 border-accent/40 bg-accent/15 px-3 text-xs text-accent hover:bg-accent hover:text-accent-foreground"
                          >
                            <Pencil className="mr-1.5 h-3 w-3" /> Write Script in Content Editor
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* FLOATING STUDIO RECORDING DECK (Active during live camera session) */}
            {cameraActive && (
              <div className="absolute bottom-2.5 sm:bottom-4 inset-x-0 z-30 flex items-center justify-center pointer-events-none px-2 sm:px-4">
                <div className="pointer-events-auto flex items-center gap-1.5 sm:gap-2.5 rounded-full border border-white/20 bg-zinc-950/95 px-3 sm:px-4 py-1.5 sm:py-2 shadow-2xl backdrop-blur-xl max-w-[calc(100%-1rem)]">
                  {/* IDLE STATE: Start Recording */}
                  {!recording && (
                    <Button
                      onClick={startRecording}
                      data-testid="camera-start"
                      className="h-8 sm:h-9 rounded-full bg-red-600 px-4 sm:px-6 text-xs sm:text-sm font-bold text-white hover:bg-red-700 shadow-lg shadow-red-900/40 transition-all hover:scale-105 shrink-0"
                    >
                      <CircleDot className="mr-1.5 sm:mr-2 h-3.5 w-3.5 sm:w-4 fill-white" /> Start Recording
                    </Button>
                  )}

                  {/* RECORDING STATE: Pause / Resume / Stop */}
                  {recording && !paused && (
                    <>
                      <Button
                        variant="outline"
                        onClick={pauseRecording}
                        data-testid="camera-pause"
                        className="h-8 rounded-full border-white/20 bg-white/10 px-3 text-xs font-semibold text-white hover:bg-white/20 shrink-0"
                      >
                        <Pause className="mr-1 h-3.5 w-3.5" /> Pause
                      </Button>
                      <Button
                        onClick={stopRecording}
                        data-testid="camera-stop"
                        className="h-8 rounded-full bg-red-600 px-3.5 sm:px-4 text-xs font-bold text-white hover:bg-red-700 shadow-md shrink-0"
                      >
                        <Square className="mr-1 h-3.5 w-3.5 fill-white" /> Stop
                      </Button>
                    </>
                  )}

                  {recording && paused && (
                    <>
                      <Button
                        onClick={resumeRecording}
                        data-testid="camera-resume"
                        className="h-8 rounded-full bg-emerald-600 px-3 text-xs font-semibold text-white hover:bg-emerald-700 shadow-md shrink-0"
                      >
                        <Play className="mr-1 h-3.5 w-3.5 fill-white" /> Resume
                      </Button>
                      <Button
                        onClick={stopRecording}
                        data-testid="camera-stop"
                        className="h-8 rounded-full bg-red-600 px-3.5 sm:px-4 text-xs font-bold text-white hover:bg-red-700 shadow-md shrink-0"
                      >
                        <Square className="mr-1 h-3.5 w-3.5 fill-white" /> Stop
                      </Button>
                    </>
                  )}

                  {/* Live Timer Indicator */}
                  <div className="border-l border-white/20 pl-2 shrink-0 flex items-center gap-1">
                    <span className="tabular-nums font-mono text-xs font-bold text-zinc-200" data-testid="camera-timer">
                      {fmtTime(seconds)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* RECORDED TAKE SHOWCASE (Renders after recording is stopped) */}
      {blob && (
        <div className="rounded-xl border border-border bg-surface p-4 sm:p-6 shadow-md" data-testid="recorded-take-section">
          <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-accent/15 text-accent">
                <CheckCircle2 className="h-4 w-4" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-display text-sm sm:text-base font-semibold text-foreground">Latest Recorded Take</h3>
                  <span className="rounded-full bg-accent/15 px-2 py-0.5 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider text-accent">
                    {qualityPreset.toUpperCase()} · {isEffectiveLandscape ? "16:9" : isEffectiveSquare ? "1:1" : "9:16"}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {(blob.size / 1024 / 1024).toFixed(2)} MB · Duration: {fmtTime(seconds)} · Format: MP4
                </p>
              </div>
            </div>
            <div className="grid grid-cols-3 sm:flex items-center gap-1.5 sm:gap-2 w-full sm:w-auto">
              <Button variant="outline" size="sm" onClick={retake} data-testid="camera-retake" className="w-full sm:w-auto h-8 px-2 sm:px-3 text-xs justify-center shrink-0 min-w-0 truncate">
                <RotateCcw className="mr-1 sm:mr-1.5 h-3.5 w-3.5 shrink-0" /> <span className="truncate">Retake</span>
              </Button>
              <Button variant="outline" size="sm" onClick={downloadTake} data-testid="camera-download" className="w-full sm:w-auto h-8 px-2 sm:px-3 text-xs justify-center shrink-0 min-w-0 truncate">
                <Download className="mr-1 sm:mr-1.5 h-3.5 w-3.5 shrink-0" /> <span className="truncate"><span className="hidden xs:inline">Download </span>MP4</span>
              </Button>
              <Button
                size="sm"
                onClick={saveAsset}
                disabled={saving}
                data-testid="camera-save-asset"
                className="w-full sm:w-auto h-8 px-2 sm:px-3 text-xs bg-accent text-accent-foreground hover:bg-accent-hover shadow-sm justify-center shrink-0 min-w-0 truncate"
              >
                <Save className="mr-1 sm:mr-1.5 h-3.5 w-3.5 shrink-0" /> <span className="truncate">{saving ? "Saving…" : "Save"}</span>
              </Button>
            </div>
          </div>

          <div className="overflow-hidden rounded-lg border border-border bg-black flex items-center justify-center p-1">
            <video
              ref={previewRef}
              src={blobUrl}
              controls
              playsInline
              webkit-playsinline="true"
              className={`w-full object-contain ${
                isEffectiveLandscape
                  ? "aspect-video max-h-[460px]"
                  : isEffectiveSquare
                  ? "aspect-square max-h-[460px] max-w-[460px] mx-auto"
                  : "aspect-[9/16] max-h-[520px] max-w-[340px] mx-auto"
              }`}
              data-testid="camera-recorded-preview"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default RecordingStudio;
